import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetchCartItems } from "../api/cart";

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to decode user ID from token
const decodeUserId = (token) => {
  try {
    return jwtDecode(token).data.user_id;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Fetch user data function
const fetchUserData = async (token) => {
  if (!token) return null;
  const userId = decodeUserId(token);
  if (!userId) return null;
  try {
    const response = await axios.get(`${API_URL}/ma-user-profile/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.user;
  } catch (error) {
    console.error("Error fetching user data:", error);
    return null;
  }
};

const useAuthStore = create(
  persist(
    (set, get) => ({
      // Authentication state
      token: null,
      error: null,

      // User data state
      userId: null,
      userData: null,

      // Authentication flag
      isAuthenticated: false,

      // Login action
      login: async (identifier, password) => {
        const formData = new FormData();
        formData.append("identifier", identifier);
        formData.append("password", password);

        try {
          const response = await axios.post(`${API_URL}/ma-login`, formData);

          if (response.status === 200) {
            const { jwt } = response.data;

            // Decode user ID
            const userId = decodeUserId(jwt);

            // Fetch user data
            const userData = await fetchUserData(jwt);

            // fetch user cart data
            await fetchCartItems(jwt);

            if (!userData) {
              throw new Error("Failed to fetch user data");
            } else {
              set({
                token: jwt,
                userId,
                userData,
                error: null,
                isAuthenticated: true,
              });
              return true;
            }
          }
        } catch (error) {
          let errorMessage = "";
          if (
            error.response?.data?.message &&
            typeof error.response.data.message === "object"
          ) {
            errorMessage = Object.values(error.response.data.message).join(
              ", "
            );
          } else {
            errorMessage = error.response?.data?.message || error.message;
          }

          set({
            token: null,
            userId: null,
            userData: null,
            error: errorMessage,
            isAuthenticated: false,
          });
          return false;
        }
      },

      // Logout action
      logout: async () => {
        const token = get().token;
        if (!token) {
          console.error("Token not found");
          return false;
        }

        try {
          const response = await axios.post(`${API_URL}/ma-logout`, null, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // fetch user cart data
          await fetchCartItems(token);

          if (response.status === 200) {
            set({
              token: null,
              userId: null,
              userData: null,
              error: null,
              isAuthenticated: false,
            });
            localStorage.removeItem("auth-storage");
            return true;
          }
        } catch (error) {
          let errorMessage = "";
          if (
            error.response?.data?.message &&
            typeof error.response.data.message === "object"
          ) {
            errorMessage = Object.values(error.response.data.message).join(
              ", "
            );
          } else {
            errorMessage = error.response?.data?.message || error.message;
          }
          console.error(`Error logging out: ${errorMessage}`);
          return false;
        }
      },

      // Method to manually refresh user data
      refreshUserData: async () => {
        const token = get().token;
        if (!token) return null;

        try {
          const userData = await fetchUserData(token);
          if (userData) {
            set({ userData });
            return userData;
          }
          return null;
        } catch (error) {
          console.error("Error refreshing user data:", error);
          return null;
        }
      },

      // Initialization of auth state
      initializeAuth: async () => {
        const token = get().token;
        if (token) {
          try {
            const userId = decodeUserId(token);
            if (userId) {
              const userData = await fetchUserData(token);
              if (userData) {
                set({
                  token,
                  userId,
                  userData,
                  error: null,
                  isAuthenticated: true,
                });
                return true;
              }
            }
          } catch (error) {
            console.error("Authentication initialization failed:", error);
          }
        }
        // Ensure flag is false if token is missing or invalid
        set({ isAuthenticated: false });
        return false;
      },
    }),
    {
      name: "auth-storage", // name of the storage (unique key)
      partialize: (state) => ({
        token: state.token,
        userId: state.userId,
        userData: state.userData,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
